import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { ConfigService } from '../config.service';
import { UserService, UserData } from '../user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly TOKEN_KEY = 'accessToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';

  private http = inject(HttpClient);
  private router = inject(Router);
  private configService = inject(ConfigService);
  private userService = inject(UserService);

  constructor() {}

  async login(usuario: string, password: string): Promise<boolean> {
    const url = `${this.configService.getCurrentApiUrl()}/login`;

    try {
      const response: any = await lastValueFrom(
        this.http.post(
          url,
          { usuario, password },
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
          }
        )
      );

      if (response && response.accessToken && response.refreshToken) {
        this.saveTokens(response.accessToken, response.refreshToken);
        
        // Guardar los datos del usuario en el UserService
        if (response.codigo) {
          const userData: UserData = {
            descricao: response.descricao || '',
            codigo: response.codigo,
            unidade: response.unidade || 0,
            tipo: response.tipo || 0,
            empresa: response.empresa || 0,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            CLIENTES: response.CLIENTES || null,
            VENDEDORES: response.VENDEDORES || null,
            CUENTA_CORRIENTE: response.CUENTA_CORRIENTE || null,
            LISTA_PRECIO: response.LISTA_PRECIO || null,
            METAS: response.METAS || null,
            PEDIDOS: response.PEDIDOS || null,
            vendedorCod: response.vendedorCod || null
          };
          this.userService.setUserData(userData);
        }
        
        return true;
      } else {
        console.error('Resposta de login inválida:', response);
        return false;
      }
    } catch (error: any) {
      console.error('Erro na requisição de login:', error.message);
      throw new Error('Credenciais inválidas ou erro de conexão.');
    }
  }

  async refreshToken(): Promise<string | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    const url = `${this.configService.getCurrentApiUrl()}/token/refresh`;

    try {
      const response: any = await lastValueFrom(
        this.http.post(url, { refreshToken })
      );
      if (response && response.accessToken) {
        this.saveAccessToken(response.accessToken);
        return response.accessToken;
      } else {
        this.logout();
        return null;
      }
    } catch (error) {
      console.error('Erro ao refrescar o token:', error);
      this.logout();
      return null;
    }
  }

  saveTokens(accessToken: string, refreshToken: string) {
    localStorage.setItem(this.TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  saveAccessToken(accessToken: string) {
    localStorage.setItem(this.TOKEN_KEY, accessToken);
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  logout() {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    this.userService.logout(); // Limpiar datos del usuario
    this.router.navigateByUrl('/login', { replaceUrl: true });
  }
}